/**
 * Audio-output routing telemetry helpers.
 *
 * Theory under test: "student sees everyone but hears nothing" is caused by
 * pinning audio elements to a persisted output device — either a device the
 * user is not actually listening to (monitor speakers, a virtual sink like
 * Teams/Zoom/Krisp), or a pin that stops following the OS default when the
 * user plugs in headphones mid-session. LiveKit itself never persists an
 * output choice; the pinning is our addition, so the telemetry's job is to
 * tag every session with its routing state and catch the divergence moments.
 *
 * The OS default is identified without label parsing: Chrome exposes a
 * `default` pseudo-device that shares a `groupId` with the physical device
 * the OS currently routes to. Browsers without that pseudo-device (Safari,
 * Firefox) report `matchesOsDefault: 'unknown'` and form their own cohort.
 */

/** The subset of livekit's Room the snapshot needs; kept minimal for tests. */
export type ActiveDeviceReader = {
  getActiveDevice(kind: MediaDeviceKind): string | undefined
}

export type AudioOutputRoutingSnapshot = {
  /** True when the room routes to an explicit sink (not '' / 'default'). */
  pinned: boolean
  pinnedDeviceId: string
  pinnedDeviceLabel: string
  pinnedDeviceClass: string
  osDefaultLabel: string
  osDefaultClass: string
  matchesOsDefault: 'yes' | 'no' | 'unknown'
  outputDeviceCount: number
  /** Labels of every enumerated output; the watcher diffs these across time. */
  outputLabels: string[]
}

const CLASS_MATCHERS: Array<[string, RegExp]> = [
  [
    'virtual',
    /virtual|vb-audio|cable|voicemeeter|blackhole|soundflower|loopback|krisp|sonar|nvidia broadcast|rtx voice|teams|zoom|discord|oculus|steam streaming|parallels|vmware/,
  ],
  ['bluetooth', /airpods|bluetooth|buds|hands-free|\bbt\b/],
  ['display', /hdmi|displayport|display audio|monitor|\btv\b|benq|viewsonic/],
  [
    'headset',
    /usb|headset|jabra|logitech|poly |plantronics|hyperx|razer|arctis|corsair|sennheiser|beats|bose|jbl|wh-10|wf-10/,
  ],
  ['builtin', /built-in|internal|macbook|realtek|conexant|cirrus/],
]

/**
 * Coarse device bucket from its label, for cohorting in analytics.
 *
 * Best-effort by construction: a label rarely says how the device is
 * connected, so `bluetooth` covers only explicit BT markers and headset
 * vendors that ship both BT and USB models (Jabra, Sennheiser, Bose, ...)
 * all land in `headset` rather than being guessed into a transport. The
 * split the investigation depends on — virtual/display sinks vs devices a
 * human plausibly wears or hears — is unaffected.
 */
export function classifyOutputDevice(label: string): string {
  const normalized = label.trim().toLowerCase()
  if (!normalized) return 'unknown'
  for (const [bucket, matcher] of CLASS_MATCHERS) {
    if (matcher.test(normalized)) return bucket
  }
  return 'other'
}

function stripDefaultPrefix(label: string | undefined): string {
  if (!label) return ''
  return label.startsWith('Default - ')
    ? label.slice('Default - '.length)
    : label
}

/**
 * Snapshot of where room audio is routed vs where the OS routes by default.
 * Never throws; returns null when device enumeration is unavailable.
 */
export async function resolveAudioOutputRoutingSnapshot(
  room: ActiveDeviceReader | null,
  enumerateDevices?: () => Promise<MediaDeviceInfo[]>
): Promise<AudioOutputRoutingSnapshot | null> {
  const enumerate =
    enumerateDevices ??
    (typeof navigator !== 'undefined' && navigator.mediaDevices
      ? () => navigator.mediaDevices.enumerateDevices()
      : null)
  if (!enumerate) return null

  let devices: MediaDeviceInfo[]
  try {
    devices = await enumerate()
  } catch {
    return null
  }

  const outputs = devices.filter((d) => d.kind === 'audiooutput')
  const activeId = room?.getActiveDevice('audiooutput') ?? ''
  const pinned = activeId !== '' && activeId !== 'default'
  const pinnedDevice = outputs.find((d) => d.deviceId === activeId)

  const defaultEntry = outputs.find((d) => d.deviceId === 'default')
  // An empty groupId is not an identity: Chrome has been seen exposing the
  // `default` device with groupId '' before mic/camera permission is granted,
  // and unlabelled devices share that same ''. Matching on it would pair the
  // default with an arbitrary empty-groupId device and report a confident
  // yes/no that is really unknown, so only trust a non-empty groupId.
  const defaultGroupId =
    defaultEntry && defaultEntry.groupId !== '' ? defaultEntry.groupId : null
  const osDefaultPhysical = defaultGroupId
    ? outputs.find(
        (d) => d.deviceId !== 'default' && d.groupId === defaultGroupId
      )
    : undefined
  const osDefaultLabel =
    osDefaultPhysical?.label || stripDefaultPrefix(defaultEntry?.label)

  let matchesOsDefault: AudioOutputRoutingSnapshot['matchesOsDefault'] =
    'unknown'
  if (!pinned) {
    matchesOsDefault = 'yes'
  } else if (defaultGroupId) {
    matchesOsDefault =
      pinnedDevice && pinnedDevice.groupId === defaultGroupId ? 'yes' : 'no'
  }

  return {
    pinned,
    pinnedDeviceId: activeId,
    pinnedDeviceLabel: pinnedDevice?.label ?? '',
    pinnedDeviceClass: classifyOutputDevice(pinnedDevice?.label ?? ''),
    osDefaultLabel,
    osDefaultClass: classifyOutputDevice(osDefaultLabel),
    matchesOsDefault,
    outputDeviceCount: outputs.filter((d) => d.deviceId !== 'default').length,
    outputLabels: outputs
      .filter((d) => d.deviceId !== 'default')
      .map((d) => d.label),
  }
}

/** Flatten a snapshot into logEvent params; tolerates a null snapshot. */
export function audioOutputSnapshotFields(
  snapshot: AudioOutputRoutingSnapshot | null
): Record<string, unknown> {
  if (!snapshot) return { output_snapshot: 'unavailable' }
  return {
    output_pinned: snapshot.pinned,
    output_device_id: snapshot.pinnedDeviceId,
    output_device_label: snapshot.pinnedDeviceLabel,
    output_device_class: snapshot.pinnedDeviceClass,
    output_os_default_label: snapshot.osDefaultLabel,
    output_os_default_class: snapshot.osDefaultClass,
    output_matches_os_default: snapshot.matchesOsDefault,
    output_device_count: snapshot.outputDeviceCount,
  }
}
